import { IonCol, IonGrid, IonRow } from "@ionic/react";
import equal from "deep-equal";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import {
  BooleanParam,
  createEnumArrayParam,
  createEnumParam,
  StringParam,
  useQueryParams,
  withDefault
} from "use-query-params";

import Checkbox from "@components/fields/Checkbox";
import EmployerSelect from "@components/fields/EmployerSelect";
import ExpertSelect from "@components/fields/ExpertSelect";
import Select from "@components/fields/Select";
import SearchInput from "@components/fieldsNative/SearchInput";
import styles from "@components/forms/AdminJourneyFilters.module.css";
import Form from "@components/forms/Form";
import TableHeaderExtraInfo from "@components/TableHeaderExtraInfo";
import { Opener } from "@hooks/useOpener";
import { ExpertJourneysExpertRolesEnum, ExpertJourneysStatusEnum } from "@typing/Generated";
import { onChangeWithPaid } from "@utils/formUtils";

type FormData = {
  employerId?: string | null;
  expertArchived?: boolean | null;
  primaryExpertId?: string | null;
  role?: ExpertJourneysExpertRolesEnum | null;
  search?: string | null;
  selfPaid?: boolean | null;
  statuses?: ExpertJourneysStatusEnum[] | null;
};

type Props = {
  archiveDefinitionOpener: Opener;
  expertFirstName?: string;
  filters: FormData;
  myJourneys: boolean;
  onChange: (filterValues: FormData) => void;
};

const ExpertJourneysFilters = ({ archiveDefinitionOpener, expertFirstName, filters, myJourneys, onChange }: Props) => {
  const intl = useIntl();

  const roleParam = createEnumParam<ExpertJourneysExpertRolesEnum>(Object.values(ExpertJourneysExpertRolesEnum));
  const statusesParam = createEnumArrayParam<ExpertJourneysStatusEnum>(Object.values(ExpertJourneysStatusEnum));

  const [query, setQuery] = useQueryParams({
    employerId: { ...StringParam, urlName: "journeyEmployerId" },
    employerName: { ...StringParam, urlName: "journeyEmployerName" },
    expertArchived: withDefault({ ...BooleanParam, urlName: "journeyExpertArchived" }, filters.expertArchived),
    primaryExpertId: { ...StringParam, urlName: "journeyPrimaryExpertId" },
    primaryExpertName: { ...StringParam, urlName: "journeyPrimaryExpertName" },
    role: withDefault({ ...roleParam, urlName: "journeyRole" }, filters.role),
    search: withDefault({ ...StringParam, urlName: "journeySearch" }, filters.search),
    selfPaid: withDefault({ ...BooleanParam, urlName: "journeySelfPaid" }, filters.selfPaid),
    statuses: withDefault({ ...statusesParam, urlName: "journeyStatuses" }, filters.statuses)
  });

  const defaultValues: FormData = useMemo(() => ({ ...filters, ...query }), [filters, query]);

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange"
  });

  const employerId = form.watch("employerId");
  const expertArchived = form.watch("expertArchived");
  const primaryExpertId = form.watch("primaryExpertId");
  const role = form.watch("role");
  const search = form.watch("search");
  const selfPaid = form.watch("selfPaid");
  const statuses = form.watch("statuses");

  useEffect(() => {
    const values = { employerId, expertArchived, primaryExpertId, role, search, selfPaid, statuses };
    if (!equal(values, filters)) {
      setQuery({ ...query, ...values });
      onChangeWithPaid(onChange, values);
    }
  }, [
    employerId,
    expertArchived,
    filters,
    onChange,
    primaryExpertId,
    query,
    role,
    search,
    selfPaid,
    setQuery,
    statuses
  ]);

  const statusOptions = useMemo(
    () =>
      [
        { nested: false, value: ExpertJourneysStatusEnum.ALL },
        { nested: false, value: ExpertJourneysStatusEnum.PRE_CONSULT },
        { nested: true, value: ExpertJourneysStatusEnum.SIGNED_UP },
        { nested: true, value: ExpertJourneysStatusEnum.AWAITING_INITIAL_CONSULT },
        { nested: true, value: ExpertJourneysStatusEnum.AWAITING_INITIAL_CONSULT_RESCHEDULE },
        { nested: false, value: ExpertJourneysStatusEnum.ACTIVE },
        { nested: true, value: ExpertJourneysStatusEnum.CARE_PLAN_IN_PROGRESS },
        { nested: true, value: ExpertJourneysStatusEnum.CARE_PLAN_PUBLISHED },
        { nested: true, value: ExpertJourneysStatusEnum.FOLLOW_UP_CONSULT_SCHEDULED },
        { nested: true, value: ExpertJourneysStatusEnum.ENGAGED },
        { nested: false, value: ExpertJourneysStatusEnum.INACTIVE }
      ].map(option => ({
        label: intl.formatMessage({ id: `models.journey.status.${option.value}` }),
        nested: option.nested,
        value: option.value
      })),
    [intl]
  );

  const roleOptions = Object.values(ExpertJourneysExpertRolesEnum).map(option => ({
    label: intl.formatMessage({ id: `models.journey.expertRoles.${option}` }),
    value: option
  }));

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <Form className={styles.form} hookForm={form} onSubmit={() => {}}>
      <IonGrid>
        <IonRow className="ion-align-items-center">
          <IonCol>
            <EmployerSelect<FormData>
              allowAny
              control={form.control}
              error={form.formState.errors.employerId}
              initialOption={{ label: query.employerName ?? "", value: query.employerId ?? "" }}
              label={intl.formatMessage({ id: "forms.expertJourneysFilters.employerLabel" })}
              name="employerId"
              onSelection={(option: { label: string }) => {
                setQuery({ ...query, ...{ employerName: option.label } });
              }}
            />
          </IonCol>
          <IonCol className="ion-margin-top" size="auto">
            <div className={styles.flex}>
              <Checkbox<FormData>
                control={form.control}
                error={form.formState.errors.expertArchived}
                name="expertArchived"
              >
                <FormattedMessage id="forms.expertJourneysFilters.expertArchivedLabel" />
              </Checkbox>
              <div className={styles.spacing}>
                <TableHeaderExtraInfo
                  descriptionKey="pages.expert.journeys.archive.description"
                  linkKey="pages.expert.journeys.archive.linkTitle"
                  onOpen={archiveDefinitionOpener.open}
                  titleKey="pages.expert.journeys.archive.title"
                />
              </div>
            </div>
          </IonCol>

          <IonCol className={statuses && statuses.length > 2 ? "ion-hide" : ""}>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.statuses}
              isMulti
              label={intl.formatMessage({ id: "forms.expertJourneysFilters.statusLabel" })}
              name="statuses"
              options={statusOptions}
              placeholder=""
            />
          </IonCol>
          <IonCol>
            <ExpertSelect<FormData>
              allowAny
              control={form.control}
              error={form.formState.errors.primaryExpertId}
              initialOption={{ label: query.primaryExpertName ?? undefined, value: query.primaryExpertId ?? null }}
              label={intl.formatMessage({ id: "forms.expertJourneysFilters.expertOwnerLabel" })}
              name="primaryExpertId"
              onSelection={(option: { label: string }) => {
                setQuery({ ...query, ...{ primaryExpertName: option.label } });
              }}
            />
          </IonCol>
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.role}
              label={
                myJourneys
                  ? intl.formatMessage({ id: "forms.expertJourneysFilters.myRoleLabel" })
                  : intl.formatMessage(
                      { id: "forms.expertJourneysFilters.expertRoleLabel" },
                      { name: expertFirstName ?? "" }
                    )
              }
              name="role"
              options={roleOptions}
            />
          </IonCol>
          <IonCol size="auto">
            <SearchInput
              error={form.formState.errors.search?.message}
              initialValue={defaultValues.search}
              name="search"
              placeholder={intl.formatMessage({ id: "forms.expertJourneysFilters.searchLabel" })}
              setValue={v => form.setValue("search", v)}
            />
          </IonCol>
        </IonRow>
        <IonRow className={statuses && statuses.length > 2 ? "" : "ion-hide"}>
          <IonCol>
            <Select<FormData>
              control={form.control}
              error={form.formState.errors.statuses}
              isMulti
              label={intl.formatMessage({ id: "forms.expertJourneysFilters.statusLabel" })}
              name="statuses"
              options={statusOptions}
              placeholder=""
            />
          </IonCol>
        </IonRow>
      </IonGrid>
    </Form>
  );
};

export default ExpertJourneysFilters;
